import { PostponeRebootOptions } from '../../../types/common/maintenanceWindowCommonTypes';
import { RecurrenceOptions } from '../../../types/common/schedulerTypes';
import { MaintenanceWindowPatchSpecificsData } from '../../../configurationProfilesApp/maintenanceWindowFormPatchSpecifics/types';
import { getTimezonedCurrentDateTimestamp } from '@nirvana/shared/time';

export class UiToDbValuesConverter {
  public static prepareSpecificsOptions(dataSpecifics: MaintenanceWindowPatchSpecificsData, isNew: boolean): Object {
    const recordSpecifics: Object = {
      downloadSettings: {},
      scanPatchesSettings: {},
      installPatchesSettings: {
        updateType: {
          securityPatchesSettings: {},
          nonSecurityPatchesSettings: {},
          manuallyApprovedPatchesSettings: {},
        },
      },
    };

    recordSpecifics['scanPatchesSettings']['enabled'] = dataSpecifics['scanForPatches'];
    if (isNew) {
      recordSpecifics['downloadSettings']['fallbackOnDirectDownload'] =
        dataSpecifics['fallbackOnDirectDownload'] ?? true;

      recordSpecifics['scanPatchesSettings']['useSameSchedule'] =
        dataSpecifics['schedulingOptions']['useSameSchedule'] ?? false;
      recordSpecifics['scanPatchesSettings']['useFallbackSchedule'] =
        dataSpecifics['schedulingOptions']['useFallbackSchedule'] ?? false;

      recordSpecifics['scanPatchesSettings']['runOnSystemChanges'] = dataSpecifics['runOnSystemChanges'] ?? true;

      recordSpecifics['installPatchesSettings']['enabled'] = dataSpecifics['applyPatches'] ?? false;
      recordSpecifics['installPatchesSettings']['ifMissedRunAsSoonAsPossible'] =
        dataSpecifics['schedulingOptions']['runAsSoonAsPossible'] ?? false;

      // specific vendors and products
      if (dataSpecifics['vendorProductsPairs'] !== undefined) {
        const vendorProductsPairs = dataSpecifics['vendorProductsPairs'];
        recordSpecifics['installPatchesSettings']['specificVendorAndProduct'] = {
          type: parseInt(dataSpecifics['vendorProductsPairsType'] ?? 1, 10),
          enabled: vendorProductsPairs.length !== 0,
          vendorProductsPairs: vendorProductsPairs,
        };
      }

      // caching servers
      recordSpecifics['downloadSettings']['cachingServers'] = dataSpecifics['cachingServers'] ?? [];

      recordSpecifics['installPatchesSettings']['updateType']['securityPatchesSettings']['enabled'] =
        dataSpecifics['patchScopeSecurity'] ?? false;
      recordSpecifics['installPatchesSettings']['updateType']['nonSecurityPatchesSettings']['enabled'] =
        dataSpecifics['patchScopeNonSecurity'] ?? false;
      recordSpecifics['installPatchesSettings']['updateType']['manuallyApprovedPatchesSettings']['enabled'] =
        dataSpecifics['patchScopeManuallyApproved'] ?? false;
    } else {
      if (typeof dataSpecifics['fallbackOnDirectDownload'] !== 'undefined') {
        recordSpecifics['downloadSettings']['fallbackOnDirectDownload'] = dataSpecifics['fallbackOnDirectDownload'];
      }

      // always update this field: if it is not visible, then it should be false
      recordSpecifics['scanPatchesSettings']['useSameSchedule'] =
        dataSpecifics['schedulingOptions']['useSameSchedule'] ?? false;

      if (typeof dataSpecifics['schedulingOptions']['useFallbackSchedule'] !== 'undefined') {
        recordSpecifics['scanPatchesSettings']['useFallbackSchedule'] =
          dataSpecifics['schedulingOptions']['useFallbackSchedule'];
      }
      if (typeof dataSpecifics['runOnSystemChanges'] !== 'undefined') {
        recordSpecifics['scanPatchesSettings']['runOnSystemChanges'] = dataSpecifics['runOnSystemChanges'];
      }
      if (typeof dataSpecifics['applyPatches'] !== 'undefined') {
        recordSpecifics['installPatchesSettings']['enabled'] = dataSpecifics['applyPatches'];
      }
      if (typeof dataSpecifics['schedulingOptions']['runAsSoonAsPossible'] !== 'undefined') {
        recordSpecifics['installPatchesSettings']['ifMissedRunAsSoonAsPossible'] =
          dataSpecifics['schedulingOptions']['runAsSoonAsPossible'];
      }

      if (
        dataSpecifics['vendorProductsPairsType'] !== undefined &&
        dataSpecifics['vendorProductsPairs'] !== undefined
      ) {
        // specific vendors and products
        const vendorProductsPairs = dataSpecifics['vendorProductsPairs'];
        recordSpecifics['installPatchesSettings']['specificVendorAndProduct'] = {
          type: parseInt(dataSpecifics['vendorProductsPairsType'], 10),
          enabled: vendorProductsPairs.length !== 0,
          vendorProductsPairs: vendorProductsPairs,
        };
      }

      if (dataSpecifics['cachingServers'] !== undefined) {
        recordSpecifics['downloadSettings']['cachingServers'] = dataSpecifics['cachingServers'];
      }

      // if applyPatches is undefined, then it is disabled, so nothing should be updated under installPatchesSettings key
      // else applyPatches can be false, so patchScopeSecurity, patchScopeNonSecurity, and patchScopeManuallyApproved may be hidden and disabled in the UI, but should be saved as false
      if (
        typeof dataSpecifics['patchScopeSecurity'] !== 'undefined' ||
        typeof dataSpecifics['applyPatches'] !== 'undefined'
      ) {
        recordSpecifics['installPatchesSettings']['updateType']['securityPatchesSettings']['enabled'] =
          dataSpecifics['patchScopeSecurity'] ?? false;
      }
      if (
        typeof dataSpecifics['patchScopeNonSecurity'] !== 'undefined' ||
        typeof dataSpecifics['applyPatches'] !== 'undefined'
      ) {
        recordSpecifics['installPatchesSettings']['updateType']['nonSecurityPatchesSettings']['enabled'] =
          dataSpecifics['patchScopeNonSecurity'] ?? false;
      }
      if (
        typeof dataSpecifics['patchScopeManuallyApproved'] !== 'undefined' ||
        typeof dataSpecifics['applyPatches'] !== 'undefined'
      ) {
        recordSpecifics['installPatchesSettings']['updateType']['manuallyApprovedPatchesSettings']['enabled'] =
          dataSpecifics['patchScopeManuallyApproved'] ?? false;
      }
    }

    // manually approved patches
    recordSpecifics['installPatchesSettings']['manuallyApprovedPatchIds'] =
      dataSpecifics['manuallyApprovedPatchIds'] ?? [];

    // postpone reboot options
    if (dataSpecifics['applyPatches'] && dataSpecifics['rebootOptions'] !== undefined) {
      recordSpecifics['installPatchesSettings']['postponeReboot'] = {};
      recordSpecifics['installPatchesSettings']['postponeReboot']['option'] = parseInt(
        dataSpecifics['rebootOptions']['rebootOption'] ?? '10',
        10
      );
      if (recordSpecifics['installPatchesSettings']['postponeReboot']['option'] === PostponeRebootOptions.INTERVAL) {
        recordSpecifics['installPatchesSettings']['postponeReboot']['afterMinutes'] = parseInt(
          dataSpecifics['rebootOptions']['additionalOptions']['interval']['minutes'],
          10
        );
        recordSpecifics['installPatchesSettings']['postponeReboot']['afterCustomMessage'] =
          dataSpecifics['rebootOptions']['additionalOptions']['interval']['customMessage'];
        recordSpecifics['installPatchesSettings']['postponeReboot']['between'] = {
          startHour: dataSpecifics['rebootOptions']['additionalOptions']['interval']['between']['start']['hours'],
          startMinute: dataSpecifics['rebootOptions']['additionalOptions']['interval']['between']['start']['minutes'],
          endHour: dataSpecifics['rebootOptions']['additionalOptions']['interval']['between']['end']['hours'],
          endMinute: dataSpecifics['rebootOptions']['additionalOptions']['interval']['between']['end']['minutes'],
        };
      }
      if (recordSpecifics['installPatchesSettings']['postponeReboot']['option'] === PostponeRebootOptions.AFTER) {
        recordSpecifics['installPatchesSettings']['postponeReboot']['afterMinutes'] = parseInt(
          dataSpecifics['rebootOptions']['additionalOptions']['after']['minutes'],
          10
        );
        recordSpecifics['installPatchesSettings']['postponeReboot']['afterCustomMessage'] =
          dataSpecifics['rebootOptions']['additionalOptions']['after']['customMessage'];
      }
    }

    // save schedulers
    if (recordSpecifics['scanPatchesSettings']['useSameSchedule']) {
      if (recordSpecifics['scanPatchesSettings']['useFallbackSchedule']) {
        recordSpecifics['scanPatchesSettings']['oldScheduler'] =
          recordSpecifics['installPatchesSettings']['updateType']['securityPatchesSettings']['oldScheduler'] =
          recordSpecifics['installPatchesSettings']['updateType']['nonSecurityPatchesSettings']['oldScheduler'] =
          recordSpecifics['installPatchesSettings']['updateType']['manuallyApprovedPatchesSettings']['oldScheduler'] =
            UiToDbValuesConverter.getSchedulerSettings(dataSpecifics['schedulingOptions'], 'all', true);
      } else {
        recordSpecifics['scanPatchesSettings']['scheduler'] =
          recordSpecifics['installPatchesSettings']['updateType']['securityPatchesSettings']['scheduler'] =
          recordSpecifics['installPatchesSettings']['updateType']['nonSecurityPatchesSettings']['scheduler'] =
          recordSpecifics['installPatchesSettings']['updateType']['manuallyApprovedPatchesSettings']['scheduler'] =
            UiToDbValuesConverter.getSchedulerSettings(dataSpecifics['schedulingOptions'], 'all', false);
      }
    } else {
      // get each scheduler
      if (recordSpecifics['scanPatchesSettings']['useFallbackSchedule']) {
        recordSpecifics['scanPatchesSettings']['oldScheduler'] = UiToDbValuesConverter.getSchedulerSettings(
          dataSpecifics['schedulingOptions'],
          'scan',
          true
        );
        recordSpecifics['installPatchesSettings']['updateType']['securityPatchesSettings']['oldScheduler'] =
          recordSpecifics['installPatchesSettings']['updateType']['manuallyApprovedPatchesSettings']['oldScheduler'] =
            UiToDbValuesConverter.getSchedulerSettings(dataSpecifics['schedulingOptions'], 'security', true);
        recordSpecifics['installPatchesSettings']['updateType']['nonSecurityPatchesSettings']['oldScheduler'] =
          UiToDbValuesConverter.getSchedulerSettings(dataSpecifics['schedulingOptions'], 'non-security', true);
      } else {
        recordSpecifics['scanPatchesSettings']['scheduler'] = UiToDbValuesConverter.getSchedulerSettings(
          dataSpecifics['schedulingOptions'],
          'scan'
        );
        recordSpecifics['installPatchesSettings']['updateType']['securityPatchesSettings']['scheduler'] =
          recordSpecifics['installPatchesSettings']['updateType']['manuallyApprovedPatchesSettings']['scheduler'] =
            UiToDbValuesConverter.getSchedulerSettings(dataSpecifics['schedulingOptions'], 'security');
        recordSpecifics['installPatchesSettings']['updateType']['nonSecurityPatchesSettings']['scheduler'] =
          UiToDbValuesConverter.getSchedulerSettings(dataSpecifics['schedulingOptions'], 'non-security');
      }
    }

    if (!dataSpecifics['patchScopeSecurity']) {
      delete recordSpecifics['installPatchesSettings']['updateType']['securityPatchesSettings']['scheduler'];
      delete recordSpecifics['installPatchesSettings']['updateType']['securityPatchesSettings']['oldScheduler'];
    }

    if (!dataSpecifics['patchScopeNonSecurity']) {
      delete recordSpecifics['installPatchesSettings']['updateType']['nonSecurityPatchesSettings']['scheduler'];
      delete recordSpecifics['installPatchesSettings']['updateType']['nonSecurityPatchesSettings']['oldScheduler'];
    }

    // Delete manually approved scheduler data when not enabled OR when security scheduler is used
    if (!dataSpecifics['patchScopeManuallyApproved'] || dataSpecifics['patchScopeSecurity']) {
      delete recordSpecifics['installPatchesSettings']['updateType']['manuallyApprovedPatchesSettings']['scheduler'];
      delete recordSpecifics['installPatchesSettings']['updateType']['manuallyApprovedPatchesSettings']['oldScheduler'];
    }

    if (!dataSpecifics['scanForPatches']) {
      delete recordSpecifics['scanPatchesSettings']['scheduler'];
      delete recordSpecifics['scanPatchesSettings']['oldScheduler'];
    }

    // nothing to update for apply patch settings on edit
    if (!isNew && dataSpecifics['applyPatches'] === undefined) {
      delete recordSpecifics['installPatchesSettings'];
    }

    return recordSpecifics;
  }

  static getSchedulerSettings(data: any, type: string, isFallback = false): Object {
    let values;
    if (isFallback) {
      switch (type) {
        case 'scan':
          values = data['fallbackSchedulers']['schedulerScanningPatches'];
          break;
        case 'security':
          values = data['fallbackSchedulers']['schedulerApplyingPatchesSecurity'];
          break;
        case 'non-security':
          values = data['fallbackSchedulers']['schedulerApplyingPatchesNonSecurity'];
          break;
        case 'all':
        default:
          values = data['fallbackSchedulers']['schedulerForAll'];
          break;
      }
      if (typeof values['recurrence'] !== 'undefined') {
        const recurrence = parseInt(values['recurrence'], 10);
        const startHour =
          recurrence === RecurrenceOptions.DAILY
            ? values['daily']['at']['hours']
            : recurrence === RecurrenceOptions.WEEKLY
            ? values['weekly']['at']['hours']
            : 21;
        const startMinute =
          recurrence === RecurrenceOptions.DAILY
            ? values['daily']['at']['minutes']
            : recurrence === RecurrenceOptions.WEEKLY
            ? values['weekly']['at']['minutes']
            : 0;
        const days = recurrence === RecurrenceOptions.WEEKLY ? values['weekly']['days'] : [1, 2, 3, 4, 5];
        return {
          recurrence: recurrence,
          days: days,
          startHour: startHour,
          startMinute: startMinute,
        };
      } else {
        return {};
      }
    } else {
      switch (type) {
        case 'scan':
          values = data['schedulerScanningPatches'];
          break;
        case 'security':
          values = data['schedulerApplyingPatchesSecurity'];
          break;
        case 'non-security':
          values = data['schedulerApplyingPatchesNonSecurity'];
          break;
        case 'all':
        default:
          values = data['schedulerForAll'];
          break;
      }
      if (typeof values['recurrence'] !== 'undefined') {
        const recurrence = parseInt(values['recurrence'], 10);
        const startingWith =
          recurrence !== RecurrenceOptions.IMMEDIATELY
            ? parseInt(values['startingWith'], 10)
            : getTimezonedCurrentDateTimestamp('UTC');
        const startDate = startingWith - new Date().getTimezoneOffset() * 60;
        const startHour = recurrence !== RecurrenceOptions.IMMEDIATELY ? values['between']['start']['hours'] : 21;
        const startMinute = recurrence !== RecurrenceOptions.IMMEDIATELY ? values['between']['start']['minutes'] : 0;
        const endHour = recurrence !== RecurrenceOptions.IMMEDIATELY ? values['between']['end']['hours'] : 23;
        const endMinute = recurrence !== RecurrenceOptions.IMMEDIATELY ? values['between']['end']['minutes'] : 59;
        let weeklyRecurrence = {};
        let monthlyRecurrence = {};

        if (recurrence === RecurrenceOptions.WEEKLY) {
          // sort days in ascending order and ensure the array contains only numbers
          weeklyRecurrence = {
            every: parseInt(values['weeklyRecurrence']['every'], 10),
            days: values['weeklyRecurrence']['days']
              .map(value => parseInt(value, 10))
              .sort(function (a, b) {
                return a - b;
              }),
          };
        }

        if (recurrence === RecurrenceOptions.MONTHLY) {
          monthlyRecurrence = {
            every: parseInt(values['monthlyRecurrence']['every'], 10),
            frequency: parseInt(values['monthlyRecurrence']['frequency'], 10),
          };
          if (monthlyRecurrence['frequency'] === 0) {
            monthlyRecurrence['frequencyDayNumber'] = values['monthlyRecurrence']['frequencyDayNumber'] ?? 0;
          } else {
            monthlyRecurrence['frequencyDayOfWeek'] = parseInt(values['monthlyRecurrence']['frequencyDayOfWeek'], 10);
          }
        }

        return {
          recurrence: recurrence,
          startDate: startDate,
          between: {
            startHour: startHour,
            startMinute: startMinute,
            endHour: endHour,
            endMinute: endMinute,
          },
          weeklyRecurrence: weeklyRecurrence,
          monthlyRecurrence: monthlyRecurrence,
        };
      } else {
        return {};
      }
    }
  }
}
